<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import facebook from "/src/assets/img/networks/facebook.svg";
import instagram from "/src/assets/img/networks/instagram.svg";
import telegram from "/src/assets/img/networks/telegram.svg";
import tikTok from "/src/assets/img/networks/tiktok.svg";
import whatsapp from "/src/assets/img/networks/whatsapp.svg";
import youtube from "/src/assets/img/networks/youtube.svg";
import xlink from "/src/assets/img/networks/youtube.svg";

const props = defineProps({
  type: String,
  brands: Array,
  up: Boolean,
  id: String,
  iconClass: { type: String, default: 'w-9 h-9' },
});

const showLinks = ref(false);
const containerRef = ref(null);

const visibleBrands = computed(() =>
  (props.brands ?? []).filter((item) => getLink(item) != null)
);

onMounted(() => {
  // Use capture phase so the listener fires BEFORE GuestLayout's @click.stop
  document.addEventListener("click", onClickOutside, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside, true);
});

function onClickOutside(event) {
  if (
    showLinks.value &&
    containerRef.value &&
    !containerRef.value.contains(event.target)
  ) {
    showLinks.value = false;
  }
}

function toggleMenu(event) {
  event.stopPropagation();
  showLinks.value = !showLinks.value;
}

function getLink(item) {
  switch (props.type) {
    case "whatsapp":
    case "whatsapp2":   return item.whatsApp_link;
    case "facebook":    return item.facebook_link;
    case "instagram":   return item.instagram_link;
    case "telegram":    return item.telegram_link;
    case "tikTok":      return item.tikTok_link;
    case "youtube":     return item.youtube_link;
    default:            return item.x_link;
  }
}

function getIcon(type) {
  switch (type) {
    case "facebook":   return facebook;
    case "instagram":  return instagram;
    case "telegram":   return telegram;
    case "tikTok":     return tikTok;
    case "whatsapp":
    case "whatsapp2":  return whatsapp;
    case "youtube":    return youtube;
    default:           return xlink;
  }
}
</script>

<template>
  <div class="relative flex items-center" ref="containerRef">

    <!-- Trigger: looks like a logo, with subtle dropdown cue -->
    <button
      type="button"
      class="flex flex-col items-center gap-0 cursor-pointer select-none focus:outline-none group"
      @click="toggleMenu"
      :aria-expanded="showLinks"
      aria-haspopup="true"
      title="Contactanos por WhatsApp"
    >
      <img
        :src="getIcon(props.type)"
        :alt="props.type"
        :class="[props.iconClass, showLinks ? 'scale-110' : '', 'object-contain transition-transform duration-200 group-hover:scale-110']"
      />
    </button>

    <!-- Dropdown panel -->
    <transition
      enter-active-class="transition ease-out duration-200 origin-top-left"
      enter-from-class="opacity-0 scale-95 translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition ease-in duration-150 origin-top-left"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="showLinks"
        class="absolute top-[calc(100%+10px)] left-0 z-[200] bg-white rounded-2xl shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)] border border-slate-100 py-2 min-w-[190px]"
      >
        <!-- Header -->
        <div class="flex items-center gap-2 px-4 pb-2 pt-1 border-b border-slate-100 mb-1">
          <img :src="getIcon(props.type)" :alt="props.type" class="w-4 h-4" />
          <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Seguinos</span>
        </div>

        <!-- Brand links -->
        <div class="flex flex-col gap-0.5 px-2">
          <a
            v-for="(brand, index) in visibleBrands"
            :key="`wa_${index}`"
            :href="getLink(brand)"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-[#d91a20] hover:text-white transition-all duration-150 group/item"
            @click="showLinks = false"
          >
            <img :src="getIcon(props.type)" :alt="props.type"
              class="w-4 h-4 shrink-0 opacity-50 group-hover/item:opacity-100 transition-opacity" />
            <span>{{ brand.name }}</span>
            <svg class="w-3 h-3 ml-auto shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
          </a>
        </div>
      </div>
    </transition>

  </div>
</template>

<style scoped>
/* No extra styles needed */
</style>
